import { ErrorHandler, Injectable, inject, isDevMode } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ENV } from '@bodyanalytics/app-config';
import { ToastService } from '../toast/toast.service';
import { safeErrorSummary } from '../../helper/safe-error-summary';
import { reportExceptionToSentry, SentryFrontendConfigI } from './sentry-error-reporting';

/**
 * GlobalErrorHandler — app-wide Angular {@link ErrorHandler}.
 *
 * Before this existed the 4 portals shipped with NO `ErrorHandler` provider, so
 * any uncaught runtime error (a template binding throwing, an unhandled promise
 * rejection, an error that escaped an NgRx store) was swallowed by Angular's
 * default handler — written to the console and otherwise invisible to the user,
 * who was left staring at a half-rendered or frozen screen.
 *
 * This handler:
 *  - surfaces an accessible, generic error toast so the user knows something
 *    went wrong (never leaks internal detail / stack traces to the UI);
 *  - logs the full error to the console in dev builds only (never in prod,
 *    to keep booking IDs, routing paths, and auth detail out of a deployed
 *    browser console — same policy as {@link SharedLogService});
 *  - forwards every uncaught error to Sentry via the {@link reportToSink} seam
 *    (#2191) — a no-op unless `SENTRY_ENABLED_FRONTEND` + `SENTRY_DSN_FRONTEND`
 *    are set; see {@link reportExceptionToSentry}.
 *
 * HTTP errors are normally handled by the functional `httpErrorInterceptor`
 * (which shows status-specific toasts and routes 401s to login). To avoid a
 * double toast, this handler suppresses the toast for `HttpErrorResponse`
 * instances — they have already been surfaced upstream — but still logs them.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly toast = inject(ToastService);
  private readonly env = inject<SentryFrontendConfigI>(ENV);

  /** User-facing copy for an unexpected client-side failure. */
  private static readonly GENERIC_MESSAGE = 'Something went wrong. Please try again.';

  handleError(error: unknown): void {
    // HTTP failures are surfaced by httpErrorInterceptor; don't double-toast.
    const isHttp = error instanceof HttpErrorResponse;

    if (!isHttp) {
      this.toast.error(GlobalErrorHandler.GENERIC_MESSAGE);
    }

    this.logInDev(error);
    this.reportToSink(error);
  }

  /**
   * Dev-only console logging. Suppressed in production builds so stack traces,
   * routing paths, and request detail never reach a deployed browser console.
   */
  private logInDev(error: unknown): void {
    if (isDevMode()) {
      console.error('[GlobalErrorHandler]', safeErrorSummary(error));
    }
  }

  /**
   * Remote error-sink seam (#2191). A no-op unless Sentry frontend reporting
   * is enabled (`SENTRY_ENABLED_FRONTEND` + `SENTRY_DSN_FRONTEND`) — see
   * {@link reportExceptionToSentry}. Every uncaught error already funnels
   * through this single method, so no call sites elsewhere need to change if
   * another sink is added later.
   */
  private reportToSink(error: unknown): void {
    reportExceptionToSentry(error, this.env);
  }
}
